import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Shot } from '../types';

interface Props {
  shot: Shot;
  onApply: () => void;
  disabled?: boolean;
  skillName?: string;
}

type Variant = 'idle' | 'processing' | 'applied' | 'failed';

interface VariantConfig {
  labelKey: string;
  color: string;
  borderColor: string;
  hoverBg: string;
}

const VARIANTS: Record<Variant, VariantConfig> = {
  idle: {
    labelKey: 'langRewriteButton.idle',
    color: 'var(--text-2)',
    borderColor: 'var(--border)',
    hoverBg: 'var(--surface-2)',
  },
  processing: {
    labelKey: 'langRewriteButton.processing',
    color: 'var(--text-2)',
    borderColor: 'var(--border)',
    hoverBg: 'transparent',
  },
  applied: {
    labelKey: 'langRewriteButton.applied',
    color: 'var(--success, #15803d)',
    borderColor: 'var(--success, #15803d)',
    hoverBg: 'rgba(21, 128, 61, 0.08)',
  },
  failed: {
    labelKey: 'langRewriteButton.failed',
    color: 'var(--danger, #b91c1c)',
    borderColor: 'var(--danger, #b91c1c)',
    hoverBg: 'rgba(185, 28, 28, 0.08)',
  },
};

const LanguageRewriteButton: React.FC<Props> = ({ shot, onApply, disabled, skillName }) => {
  const { t } = useTranslation('common');
  const status = (shot.languageRewriteStatus ?? 'idle') as Variant;
  const variant = VARIANTS[status];
  const isProcessing = status === 'processing';
  const [hovered, setHovered] = React.useState(false);

  const name = skillName && skillName.trim() ? skillName : t('langRewriteButton.currentSkill');

  const buildTitle = (): string => {
    switch (status) {
      case 'processing':
        return t('langRewriteButton.titleProcessing', { name });
      case 'applied':
        return t('langRewriteButton.titleApplied', { name });
      case 'failed': {
        if (shot.languageRewriteError) {
          return t('langRewriteButton.titleFailedWithError', { name, error: shot.languageRewriteError });
        }
        return t('langRewriteButton.titleFailed', { name });
      }
      case 'idle':
      default:
        return t('langRewriteButton.titleIdle', { name });
    }
  };

  const buttonStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    background: hovered && !isProcessing && !disabled ? variant.hoverBg : 'transparent',
    color: variant.color,
    border: `1px solid ${variant.borderColor}`,
  };

  return (
    <button
      type="button"
      className="btn"
      onClick={isProcessing ? undefined : onApply}
      disabled={disabled || isProcessing}
      title={buildTitle()}
      style={buttonStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isProcessing && <span className="loading-spinner" />}
      {t(variant.labelKey)}
    </button>
  );
};

export default LanguageRewriteButton;
